import { useEffect, useRef, useState } from 'react';
import { getLastSyncTime } from '@/core/offline-store';
import { usePixelDrift } from './AntiBurnIn';

// ── Props ──────────────────────────────────────────────────────────

interface SensarIdleProps {
  boardCode?: string;
  location?: string;
}

// ── Design tokens (mirrors ProvisionScreen) ────────────────────────

const COL = {
  bg: '#0A0A0A',
  bone: '#F4F1EC',
  orange: '#FF6B2B',
  graySoft: 'rgba(244,241,236,0.55)',
  grayFaint: 'rgba(244,241,236,0.32)',
  rule: 'rgba(244,241,236,0.08)',
  panel: 'rgba(244,241,236,0.04)',
};

const FONT_DISPLAY = "'Space Grotesk', sans-serif";
const FONT_MONO = "'JetBrains Mono', ui-monospace, monospace";

// Pulse cadence — full ring expansion every PULSE_PERIOD_MS.
const PULSE_PERIOD_MS = 3_400;

// ── Connectivity (real, not "location is empty") ───────────────────

type ConnStatus = 'online' | 'stale' | 'offline';

const FRESH_MS = 5 * 60 * 1000;

function useConnectivity(): ConnStatus {
  const [status, setStatus] = useState<ConnStatus>(() => computeConn());
  useEffect(() => {
    const recompute = () => setStatus(computeConn());
    window.addEventListener('online', recompute);
    window.addEventListener('offline', recompute);
    const id = window.setInterval(recompute, 5_000);
    return () => {
      window.removeEventListener('online', recompute);
      window.removeEventListener('offline', recompute);
      window.clearInterval(id);
    };
  }, []);
  return status;
}

function computeConn(): ConnStatus {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return 'offline';
  const last = getLastSyncTime();
  if (!last) return 'stale';
  return Date.now() - last < FRESH_MS ? 'online' : 'stale';
}

const CONN_DECOR: Record<ConnStatus, { label: string; color: string }> = {
  online: { label: 'ONLINE', color: '#22C55E' },
  stale: { label: 'STALE', color: '#F59E0B' },
  offline: { label: 'OFFLINE', color: '#EF4444' },
};

// ── Time hook — wall clock + animation phase + app uptime ──────────

function useClock(): { now: Date; appUptimeMs: number; phase: number } {
  const start = useRef(Date.now());
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      setTick(performance.now());
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
  const now = new Date();
  const appUptimeMs = Date.now() - start.current;
  const phase = (tick % PULSE_PERIOD_MS) / PULSE_PERIOD_MS; // 0..1
  return { now, appUptimeMs, phase };
}

// ── Background chrome (radial vignette + drifting grid) ────────────

function BackgroundChrome({ phase }: { phase: number }) {
  const drift = phase * 60;
  return (
    <>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(1200px 700px at 50% 45%, rgba(255,107,43,0.08), transparent 65%)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(244,241,236,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(244,241,236,0.035) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage:
            'radial-gradient(900px 600px at 50% 50%, #000 30%, transparent 80%)',
          WebkitMaskImage:
            'radial-gradient(900px 600px at 50% 50%, #000 30%, transparent 80%)',
          transform: `translate(${drift}px, ${drift / 1.7}px)`,
          pointerEvents: 'none',
        }}
      />
    </>
  );
}

// ── Center piece — pulse rings around the Sensar mark ──────────────
//
// Three rings, evenly phase-offset by 1/3 cycle so the screen always
// has something gently expanding. Calm + branded + zero flicker —
// what a signage screen should feel like in idle.

function PulseRings({ phase }: { phase: number }) {
  const rings = [0, 1 / 3, 2 / 3];

  return (
    <div
      style={{
        position: 'relative',
        width: 360,
        height: 360,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {rings.map((offset, i) => {
        let p = (phase + offset) % 1;
        // Ease out — fast start, slow finish — feels like a real sonar ping
        const eased = 1 - Math.pow(1 - p, 2.2);
        const size = 80 + eased * 320;
        const opacity = (1 - p) * 0.85;
        const width = 1 + (1 - p) * 1;
        void offset;
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: size,
              height: size,
              borderRadius: '50%',
              border: `${width}px solid rgba(255,107,43,${(opacity * 0.85).toFixed(3)})`,
              boxShadow: `0 0 ${24 * (1 - p)}px rgba(255,107,43,${(opacity * 0.4).toFixed(3)})`,
              pointerEvents: 'none',
            }}
          />
        );
      })}

      {/* Static "core" ring + glow that holds the brand mark steady */}
      <div
        style={{
          position: 'absolute',
          width: 144,
          height: 144,
          borderRadius: '50%',
          background:
            'radial-gradient(closest-side, rgba(255,107,43,0.16), transparent 70%)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          width: 92,
          height: 92,
          borderRadius: '50%',
          background: 'rgba(10,10,10,0.85)',
          border: `1px solid ${COL.rule}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 0 30px rgba(255,107,43,${(0.18 + Math.sin(phase * Math.PI * 2) * 0.06).toFixed(3)})`,
        }}
      >
        <Mark height={36} />
      </div>
    </div>
  );
}

// ── Inline Sensar mark (without lockup) ────────────────────────────

function Mark({ height = 28 }: { height?: number }) {
  return (
    <svg
      width={height * 1.43}
      height={height}
      viewBox="0 0 220 154"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Sensar"
    >
      <path
        d="M219.634 82.8357C219.634 56.6668 198.42 35.4631 172.261 35.4631C159.763 35.4631 148.415 40.3112 139.944 48.2066C136.577 21.6434 114.532 0.279923 87.1909 0.0028903C57.037 -0.306108 32.5302 24.2006 32.8392 54.3546C33.1375 82.8144 56.2698 105.531 84.3247 107.428L84.6976 130.219C84.6976 142.952 95.0224 153.277 107.755 153.277H174.957C187.69 153.277 198.015 142.952 198.015 130.219C198.015 127.8 197.642 125.467 196.949 123.282C210.556 114.961 219.634 99.9585 219.634 82.8464V82.8357Z"
        fill={COL.bone}
      />
      <path
        d="M84.687 112.18V130.208C84.687 142.941 95.0117 153.266 107.745 153.266L84.6976 153.17H43.6967C20.1276 153.17 0.0640911 134.502 0.00016043 110.933C-0.0637702 87.4601 18.9875 68.4194 42.4608 68.4726C66.0299 68.5366 84.6976 88.6001 84.6976 112.169V125.2L84.687 112.169V112.18Z"
        fill={COL.orange}
      />
      <path
        d="M101.693 114.066C102.023 115.302 102.609 116.421 103.792 117.241C105.784 118.626 108.373 118.243 110.504 117.412C122.619 112.691 135.245 106.831 144.792 97.8275C146.018 96.6767 147.2 95.4194 147.861 93.8744C149.225 90.6779 147.254 87.6518 144.92 85.5527C144.398 85.0839 143.844 84.6364 143.301 84.1995C134.148 76.8795 124.004 71.4347 113.477 66.3949C110.611 65.0203 107.02 64.0614 103.994 65.6277C100.904 67.2366 101 70.6036 100.755 73.6084C100.403 78.0196 100.201 82.4414 100.137 86.874C100.073 91.477 100.169 96.0907 100.414 100.694C100.648 105.03 100.584 109.857 101.693 114.066Z"
        fill={COL.bg}
      />
    </svg>
  );
}

// ── Wordmark for the top bar (mark + text) ─────────────────────────

function Wordmark({ height = 24 }: { height?: number }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <Mark height={height} />
      <span
        style={{
          fontFamily: FONT_DISPLAY,
          fontWeight: 600,
          fontSize: height * 0.7,
          letterSpacing: 0.4,
          color: COL.bone,
        }}
      >
        sensar
      </span>
    </span>
  );
}

// ── Status pill (connectivity) ─────────────────────────────────────

function StatusPill({
  decor,
  pulse,
}: {
  decor: { label: string; color: string };
  pulse: boolean;
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '5px 12px',
        borderRadius: 999,
        border: `1px solid ${decor.color}55`,
        backgroundColor: `${decor.color}14`,
        color: decor.color,
        fontFamily: FONT_MONO,
        fontSize: 11,
        letterSpacing: 2,
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: decor.color,
          boxShadow: `0 0 8px ${decor.color}80`,
          animation: pulse ? 'sensar-dot-pulse 1.8s ease-out infinite' : undefined,
        }}
      />
      {decor.label}
    </span>
  );
}

// ── Helpers ────────────────────────────────────────────────────────

function formatUptime(ms: number): string {
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

function formatTime(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function getBundleVersion(): string {
  const v = (globalThis as { __SENSAR_BUNDLE_VERSION__?: string })
    .__SENSAR_BUNDLE_VERSION__;
  return v ?? 'dev';
}

// ── Main ──────────────────────────────────────────────────────────

export default function SensarIdle({ boardCode, location }: SensarIdleProps) {
  const conn = useConnectivity();
  const { now, appUptimeMs, phase } = useClock();
  const connDecor = CONN_DECOR[conn];

  const drift = usePixelDrift();

  return (
    <div style={{ ...styles.root, ...drift }}>
      <BackgroundChrome phase={phase} />

      {/* Top bar */}
      <div style={styles.topBar}>
        <Wordmark height={26} />
        <span style={styles.topInfo}>
          {boardCode && (
            <span style={styles.boardChip}>
              {boardCode.toUpperCase()}
              {location ? ` · ${location.toUpperCase()}` : ''}
            </span>
          )}
          <StatusPill decor={connDecor} pulse={conn === 'online'} />
        </span>
      </div>

      {/* Center stack */}
      <div style={styles.center}>
        <div style={styles.eyebrow}>STANDBY</div>
        <h1 style={styles.title}>Awaiting next campaign</h1>
        <p style={styles.subtitle}>
          The display will resume as soon as the schedule serves a creative
          for this slot. No interaction required.
        </p>

        <div style={{ marginTop: 56 }}>
          <PulseRings phase={phase} />
        </div>
      </div>

      {/* Footer rail */}
      <div style={styles.footer}>
        <span style={styles.footerCell}>SENSAR PLAYER</span>
        <span style={styles.dotSep}>·</span>
        <span style={styles.footerCell}>v{getBundleVersion()}</span>
        <span style={{ flex: 1 }} />
        <span style={styles.footerCell}>UPTIME {formatUptime(appUptimeMs)}</span>
        <span style={styles.dotSep}>·</span>
        <span style={styles.footerCell}>{formatTime(now)}</span>
      </div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  root: {
    position: 'fixed',
    inset: 0,
    backgroundColor: COL.bg,
    color: COL.bone,
    fontFamily: FONT_DISPLAY,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  topBar: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '28px 40px',
    zIndex: 2,
  },
  topInfo: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 14,
  },
  boardChip: {
    padding: '5px 12px',
    borderRadius: 999,
    backgroundColor: COL.panel,
    border: `1px solid ${COL.rule}`,
    color: COL.graySoft,
    fontFamily: FONT_MONO,
    fontSize: 11,
    letterSpacing: 1.6,
  },
  center: {
    position: 'relative',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 40px',
    textAlign: 'center',
    zIndex: 2,
  },
  eyebrow: {
    fontFamily: FONT_MONO,
    fontSize: 11,
    color: COL.orange,
    letterSpacing: 4,
    marginBottom: 14,
  },
  title: {
    margin: 0,
    fontSize: 64,
    fontWeight: 600,
    letterSpacing: -1.2,
    lineHeight: 1.05,
    color: COL.bone,
  },
  subtitle: {
    marginTop: 18,
    maxWidth: 560,
    color: COL.graySoft,
    fontSize: 16,
    lineHeight: 1.65,
  },
  footer: {
    padding: '24px 40px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontFamily: FONT_MONO,
    fontSize: 11,
    letterSpacing: 2,
    color: COL.grayFaint,
    zIndex: 2,
  },
  footerCell: {
    color: COL.grayFaint,
  },
  dotSep: {
    color: COL.grayFaint,
  },
};

// ── Keyframes (shared with ProvisionScreen) ────────────────────────

if (typeof document !== 'undefined' && !document.getElementById('sensar-idle-kf')) {
  const style = document.createElement('style');
  style.id = 'sensar-idle-kf';
  style.textContent = `
    @keyframes sensar-dot-pulse {
      0%   { box-shadow: 0 0 0 0   rgba(34,197,94,0.55); }
      70%  { box-shadow: 0 0 0 10px rgba(34,197,94,0);  }
      100% { box-shadow: 0 0 0 0    rgba(34,197,94,0);  }
    }
    @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  `;
  document.head.appendChild(style);
}
